import React from 'react';
import { store } from 'redux/store';
import { useState, useEffect } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { Router } from 'react-router-dom';
import { ApolloClient, InMemoryCache } from '@apollo/client';
import { createBrowserHistory } from 'history';
import { Notify } from 'react-redux-notify';
import { ThemeProvider } from '@material-ui/core/styles';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import MomentUtils from '@date-io/moment';
import { theme as lightTheme } from './theme';
import 'react-perfect-scrollbar/dist/css/styles.css';
import './assets/scss/index.scss';
import 'react-redux-notify/dist/ReactReduxNotify.css';
import Routes from './Routes';
import { ErrorView } from './views/ErrorView';
import { BugTracker } from 'Utils/Bugtracker';
import { UPDATE_DARK_MODE } from 'redux/actions/types';

import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import {
  msalConfig,
  resolveAzureSpaClientId
} from './services/microsoft/authConfig';
import { apolloClient } from 'graphql/apolloClient';
import { Toaster } from 'react-hot-toast';
import CalendarNotificationsListener from 'components/Notifications/CalendarNotificationsListener';
import ChatterNotificationsListener from 'components/Notifications/ChatterNotificationsListener';
import { globalHost } from 'helpers';
import { QueryClientProvider } from 'react-query';
import { queryClient } from './queryClient';
import { useExpiration } from 'components/Timer';

const msalInstance = new PublicClientApplication(msalConfig);
const ErrorBoundary = BugTracker.getPlugin('react').createErrorBoundary(React);
export const browserHistory = createBrowserHistory();
const LegacyDialog = React.lazy(
  () => import('components/LegacyDealsViewer/components/LegacyDialog')
);

const LazyLegacyDialog = () => {
  const isOpen = useSelector<any, boolean>(
    (state) => state.legacyDealReducer.legacyBool
  );

  if (!isOpen) return null;

  return (
    <React.Suspense fallback={null}>
      <LegacyDialog />
    </React.Suspense>
  );
};

const App = ({ reloadTheme, userSocket }: any) => {
  useExpiration();
  const dispatch = useDispatch();
  const [theme, setTheme] = useState(lightTheme);

  useEffect(() => {
    const updatedTheme = store.getState().config.theme;
    setTheme(updatedTheme);
  }, [reloadTheme]);

  useEffect(() => {
    dispatch({
      type: UPDATE_DARK_MODE,
      payload: userSocket && userSocket?.darkMode ? 'dark' : 'light'
    });
  }, [userSocket]);

  useEffect(() => {
    const resolvedHost = globalHost(window.location);
    const resolvedAzureSpaClient = resolveAzureSpaClientId(
      window.location.origin
    );
    const reactAppEnvs = Object.keys(process.env)
      .filter((key) => key.startsWith('REACT_APP_'))
      .sort()
      .reduce(
        (acc, key) => {
          acc[key] = process.env[key];
          return acc;
        },
        {} as Record<string, string | undefined>
      );

    console.info('[BIPS] Frontend booted', {
      origin: window.location.origin,
      buildBranch: process.env.REACT_APP_BUILD_BRANCH,
      deployedAt: process.env.REACT_APP_BUILD_TIME_UK,
      resolvedBaseUrl: resolvedHost.baseURL,
      resolvedHostName: resolvedHost.globalHostName,
      resolvedAzureSpaClient,
      reactAppEnvs
    });
  }, []);

  (window as any).__APOLLO_CLIENT__ = apolloClient;
  return (
    <QueryClientProvider client={queryClient}>
      <MuiPickersUtilsProvider utils={MomentUtils}>
        <ThemeProvider theme={theme}>
          <MsalProvider instance={msalInstance}>
            <ErrorBoundary FallbackComponent={ErrorView} onError={console.log}>
              <Router history={browserHistory}>
                <LazyLegacyDialog />
                <CalendarNotificationsListener />
                <ChatterNotificationsListener />
                <Routes />
                <Notify customStyles={{ background: 'red', color: 'yellow' }} />
                <Toaster />
              </Router>
            </ErrorBoundary>
          </MsalProvider>
        </ThemeProvider>
      </MuiPickersUtilsProvider>
    </QueryClientProvider>
  );
};

const mapStateToProps = (state) => ({
  reloadTheme: state.config.theme.palette,
  userSocket: state.fb.proxy_settings
});

export default connect(mapStateToProps)(App);

export const RedflagClient = new ApolloClient({
  cache: new InMemoryCache(),
  uri: `https://azp-primary-api.azurewebsites.net/graphql`,
  connectToDevTools: true
});
